/**
 * Состояние связи с облаком и школьным сервером.
 *
 * Облако отвечает за учёт: 401 — сессия, 503/504 с school_unavailable — школа,
 * 5xx — облако, отсутствие ответа — сеть устройства. Разбор ответа живёт в
 * services/Api.js, здесь только состояние и текст информационного экрана.
 *
 * Пока связи со школой нет, работать нельзя: экран блокируется
 * (ConnectionBanner), данные не подменяются сохранёнными копиями. При
 * восстановлении связи вьюхи перечитывают данные по reloadToken.
 */

export const UP = "up";
export const UNKNOWN = "unknown";
export const SCHOOL_DOWN = "school_down";
export const CLOUD_DOWN = "cloud_down";
export const OFFLINE = "offline";

export const DOWN_STATES = [SCHOOL_DOWN, CLOUD_DOWN, OFFLINE];

export const BANNERS = {
  [SCHOOL_DOWN]: {
    variant: "warning",
    title: "Нет связи со школьным сервером",
    text:
      "Личный кабинет открыт, но школьный сервер не отвечает. Пока связи нет, " +
      "данные класса и оформление заявок недоступны."
  },
  [CLOUD_DOWN]: {
    variant: "danger",
    title: "Сервис временно недоступен",
    text:
      "Не отвечает сервис личного кабинета — это не школьный сервер. " +
      "Попробуйте обновить страницу позже."
  },
  [OFFLINE]: {
    variant: "danger",
    title: "Нет соединения с интернетом",
    text: "Устройство не в сети. Проверьте подключение."
  }
};

export default {
  namespaced: true,

  state: {
    status: UNKNOWN,
    reason: null,
    since: null,
    lastOkAt: null,
    schoolName: null,
    probing: false,
    reloadToken: 0,
    recoveredAt: null
  },

  getters: {
    isDown(state) {
      return DOWN_STATES.includes(state.status);
    },

    bannerInfo(state) {
      return BANNERS[state.status] || null;
    }
  },

  mutations: {
    setStatus(state, { status, reason }) {
      if (state.status !== status) {
        state.since = status === UP ? null : Date.now();
      }

      state.status = status;
      state.reason = reason || null;
    },

    setLastOk(state, at) {
      state.lastOkAt = at;
    },

    setSchoolName(state, name) {
      state.schoolName = name || null;
    },

    setProbing(state, value) {
      state.probing = value;
    },

    setRecovered(state, at) {
      state.recoveredAt = at;
    },

    bumpReload(state) {
      state.reloadToken += 1;
    }
  },

  actions: {
    /**
     * Успешный ответ школы означает, что она на связи. Первый успех после сбоя
     * возвращает рабочий экран и просит вьюхи перечитать данные.
     */
    success({ commit, state }) {
      const wasDown = DOWN_STATES.includes(state.status);

      commit("setLastOk", Date.now());
      commit("setStatus", { status: UP, reason: null });

      if (wasDown) {
        commit("setRecovered", Date.now());
        commit("bumpReload");
      }
    },

    failure({ commit }, { kind, reason }) {
      commit("setStatus", { status: kind, reason });
    },

    /** Название школы из ответа облака — чтобы было понятно, с какой школой нет связи. */
    schoolKnown({ commit }, name) {
      commit("setSchoolName", name);
    },

    probeStarted({ commit }) {
      commit("setProbing", true);
    },

    probeFinished({ commit }) {
      commit("setProbing", false);
    },

    /** Ручная проверка связи и перечитывание данных текущего экрана. */
    bumpReload({ commit }) {
      commit("bumpReload");
    }
  }
};
